import React, { useEffect, useState } from "react";
import { Music, Bell, Loader2, Heart, Play, Lock, Share2, Sparkles } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { IMG } from "@/lib/images";
import { api } from "@/lib/api";
import { useEmergency } from "@/components/EmergencyProvider";
import { toast } from "sonner";

const RELEASE = new Date("2027-01-01T00:00:00");

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const diff = Math.max(0, target.getTime() - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return { days, hours, mins, secs };
}

const LYRICS = [
  "When the night feels like it's winning,",
  "and the quiet turns to stone —",
  "put the weight down for a minute,",
  "you were never meant to carry it alone.",
  "So stay… just stay.",
  "The morning's on its way.",
];

export default function Stay() {
  const { open } = useEmergency();
  const { days, hours, mins, secs } = useCountdown(RELEASE);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const notify = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your email."); return; }
    setLoading(true);
    try {
      await api.post("/subscribe", { email, tag: "stay" });
      toast.success("You're on the list! We'll tell you the moment 'Stay' is live. 🎵");
      setEmail("");
    } catch { toast.error("Something went wrong. Please try again."); }
    setLoading(false);
  };

  const share = async () => {
    const data = { title: "STAY — a song for suicide prevention", text: "A new song written for everyone who's hurting. Coming January 2027.", url: window.location.href };
    try { if (navigator.share) await navigator.share(data); else { await navigator.clipboard.writeText(window.location.href); toast.success("Link copied to share!"); } } catch {}
  };

  const Box = ({ v, l }) => (
    <div className="flex flex-col items-center">
      <span className="font-display text-4xl sm:text-6xl font-bold gold-text tabular-nums">{String(v).padStart(2, "0")}</span>
      <span className="text-[11px] font-mono uppercase tracking-widest text-bsn-ice/60 mt-1">{l}</span>
    </div>
  );

  return (
    <div data-testid="stay-page">
      {/* Hero */}
      <section className="relative bg-bsn-midnight text-bsn-parchment overflow-hidden">
        <img src={IMG.stayCover} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-bsn-midnight/80 via-bsn-midnight/85 to-bsn-midnight" />
        <div className="relative max-w-6xl mx-auto px-4 py-20 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative mx-auto max-w-sm">
              <img src={IMG.stayCover} alt="STAY single cover" className="w-full rounded-2xl shadow-2xl ring-1 ring-bsn-gold/40 float-soft" data-testid="stay-cover" />
              <span className="absolute -top-3 -right-3 bg-bsn-gold text-bsn-midnight text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">NEW SINGLE</span>
            </div>
          </Reveal>
          <div>
            <Reveal><p className="eyebrow text-bsn-gold mb-4 flex items-center gap-2"><Music className="w-4 h-4" /> A Song for Suicide Prevention</p></Reveal>
            <Reveal delay={80}>
              <h1 className="font-display text-6xl sm:text-7xl font-bold gold-text tracking-wide">STAY</h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-5 text-lg text-bsn-ice/85 leading-relaxed max-w-lg">
                A tender, soaring anthem written for everyone standing at the edge of a hard decision — a hand on your
                shoulder set to melody, whispering the one word that changes everything: <em className="text-bsn-champagne">stay.</em>
              </p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-8 inline-flex items-center gap-3 bg-bsn-navy/60 border border-bsn-gold/30 rounded-2xl px-6 py-5">
                <Box v={days} l="Days" /><span className="text-bsn-gold/50 text-3xl font-light">:</span>
                <Box v={hours} l="Hrs" /><span className="text-bsn-gold/50 text-3xl font-light">:</span>
                <Box v={mins} l="Min" /><span className="text-bsn-gold/50 text-3xl font-light">:</span>
                <Box v={secs} l="Sec" />
              </div>
            </Reveal>
            <Reveal delay={320}>
              <p className="mt-4 font-serif text-xl text-bsn-champagne">Releasing January 2027</p>
            </Reveal>
            <Reveal delay={380}>
              <p className="mt-2 text-sm text-bsn-ice/70 font-mono tracking-wide" data-testid="stay-credits">
                Music &amp; lyrics written by <span className="text-bsn-champagne">Mary Bright</span> &amp; <span className="text-bsn-champagne">Anthony Brown</span>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Preview player + notify */}
      <section className="max-w-4xl mx-auto px-4 py-16 sm:py-20">
        <Reveal>
          <div className="bg-white rounded-3xl p-8 sm:p-10 card-tactile">
            <div className="flex items-center gap-5">
              <img src={IMG.stayCover} alt="STAY" className="w-20 h-20 rounded-xl object-cover shadow-md" />
              <div className="flex-1">
                <p className="font-serif text-2xl font-semibold text-bsn-midnight">Stay</p>
                <p className="text-sm text-bsn-sapphire/70">Bright Stories Network · Single</p>
              </div>
              <button onClick={() => toast("🔒 'Stay' unlocks January 2027. Join the list below!", { description: "Thank you for your patience and your heart." })}
                className="w-16 h-16 rounded-full btn-gold flex items-center justify-center relative" data-testid="stay-preview-play" aria-label="Preview locked">
                <Lock className="w-6 h-6" />
              </button>
            </div>
            <div className="mt-6 h-2 rounded-full bg-bsn-canvas overflow-hidden">
              <div className="h-full w-0 bg-gradient-to-r from-bsn-gold to-bsn-amber" />
            </div>
            <p className="text-center text-xs text-bsn-sapphire/60 mt-3">The full song unlocks on release day.</p>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <div className="mt-8 bg-bsn-midnight text-bsn-parchment rounded-3xl p-8 sm:p-10 text-center">
            <Bell className="w-9 h-9 text-bsn-gold mx-auto mb-4" />
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold">Be the first to hear it</h2>
            <p className="mt-3 text-bsn-ice/80">Join the waitlist and we'll send you "Stay" the moment it drops.</p>
            <form onSubmit={notify} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="stay-notify-form">
              <Input type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-bsn-navy border-bsn-gold/30 text-bsn-parchment placeholder:text-bsn-ice/40" data-testid="stay-email-input" />
              <Button type="submit" disabled={loading} className="btn-gold font-semibold shrink-0" data-testid="stay-notify-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Notify Me</>}
              </Button>
            </form>
            <button onClick={share} className="mt-5 inline-flex items-center gap-2 text-sm text-bsn-ice/70 hover:text-bsn-gold transition-colors" data-testid="stay-share-btn">
              <Share2 className="w-4 h-4" /> Share the message of "Stay"
            </button>
          </div>
        </Reveal>
      </section>

      {/* Story + lyrics */}
      <section className="bg-bsn-canvas border-y border-bsn-gold/20">
        <div className="max-w-4xl mx-auto px-4 py-16 sm:py-20" data-testid="behind-the-song">
          <Reveal>
            <p className="eyebrow mb-3">Behind the Song</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-bsn-midnight leading-tight">STAY: When Music Becomes a Message of Hope</h2>
            <p className="mt-3 font-mono text-xs sm:text-sm tracking-wider uppercase text-bsn-bronze">Mary Bright &amp; Anthony Brown &nbsp;|&nbsp; A Musical Collaboration</p>
            <div className="mt-8 text-bsn-sapphire/85 text-base sm:text-lg leading-relaxed space-y-3">
              <figure className="sm:float-left sm:mr-8 sm:mb-4 w-full sm:w-72 mb-6" data-testid="stay-artists-photo">
                <div className="rounded-2xl overflow-hidden border border-bsn-gold/50 shadow-xl ring-1 ring-bsn-gold/20">
                  <img src="/stay/mary-anthony-2.jpg" alt="Mary Bright and Anthony Brown with a saxophone in a jazz lounge" className="w-full h-auto object-cover" />
                </div>
                <figcaption className="mt-3 text-base sm:text-lg text-center font-serif italic font-semibold text-bsn-bronze">Mary Bright &amp; Anthony Brown</figcaption>
              </figure>
              <p className="first-letter:font-serif first-letter:text-5xl first-letter:font-semibold first-letter:text-bsn-gold first-letter:float-left first-letter:mr-2 first-letter:leading-none">Some songs are written to entertain. Others are born from a deeper purpose: to touch hearts, inspire hope, and perhaps even save a life. <strong className="text-bsn-midnight">STAY</strong> is one of those songs.</p>
              <p>The story behind STAY began years ago, when Mary Bright and Anthony Brown were students at the same historically Black college, both sharing a passion for the saxophone. Although life eventually carried them in different directions, a special connection remained through their mutual friend and fellow classmate, Otis Cuyler, an extraordinarily gifted musician in his own right.</p>
              <p>Years later, during a visit with Otis, Anthony learned about Mary's suicide prevention campaign. Moved by her mission, he offered his musical talents to help transform a message of hope into something that could reach people through the universal language of music.</p>
              <p>A professional musician since his youth, Anthony brings decades of experience as a performer and educator. Most recently, he taught music to elementary school students, passing his passion for music on to a new generation.</p>
              <p>Mary, a poet, spoken word artist, Gullah Geechee linguist, and humanitarian, uses her creative voice to illuminate life's struggles, inspire resilience, and remind others of the power of hope.</p>
              <p>Together, they bring their musical talents and shared compassion to STAY, a song created to encourage those facing life's darkest moments to hold on to hope.</p>
              <p>STAY marks the beginning of an exciting creative collaboration, with more musical productions planned for release soon.</p>
              <p className="clear-both pt-2 font-serif text-xl sm:text-2xl font-semibold text-bsn-midnight text-center" data-testid="stay-closing-line">Two artists. One shared purpose. A message worth hearing.</p>
              <div className="pt-6">
                <p className="text-center text-bsn-sapphire/80" data-testid="stay-release-note">When it releases in January 2027, a portion of every stream and purchase will support suicide-prevention outreach.</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-14 max-w-2xl mx-auto bg-bsn-midnight text-bsn-parchment rounded-3xl p-8 border border-bsn-gold/25">
              <Sparkles className="w-7 h-7 text-bsn-gold mb-4" />
              <p className="eyebrow text-bsn-gold mb-4">A first glimpse of the lyrics</p>
              <div className="space-y-2 font-serif text-xl leading-relaxed text-bsn-champagne italic">
                {LYRICS.map((l, i) => <p key={i}>{l}</p>)}
              </div>
              <p className="mt-6 pt-4 border-t border-bsn-gold/20 text-xs font-mono tracking-wide text-bsn-ice/60">
                Music &amp; lyrics by Mary Bright &amp; Anthony Brown
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Crisis note */}
      <section className="max-w-3xl mx-auto px-4 py-16 text-center">
        <Heart className="w-8 h-8 text-bsn-gold mx-auto mb-4" />
        <p className="font-serif text-2xl text-bsn-midnight leading-snug">If the message of "Stay" is one you need right now, please don't wait for January.</p>
        <button onClick={open} className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full btn-gold font-semibold transition-all" data-testid="stay-crisis-btn">
          <Play className="w-5 h-5" /> Reach Support Now
        </button>
      </section>
    </div>
  );
}
